import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import AuthService, {
  User,
  AuthError,
  LoginRequest,
  RegisterWithEmailVerificationRequest,
  RegisterWithEmailVerificationResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  MFAEnableRequest,
  MFAEnableResponse,
  MFADisableRequest,
  MFADisableResponse,
  MFAVerifyRequest,
  MFAVerifyResponse,
  MFAStatusResponse,
  MFAGenerateBackupCodesRequest,
  MFAGenerateBackupCodesResponse
} from '../services/authService';
import { generateSecureSessionId } from '../utils/crypto';
import { AccountDataService } from '../services/accountDataService';
import { InitialDataService } from '../services/initialDataService';

// 認証状態の型定義
export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  isInitializing: boolean;
  error: string | null;
  sessionId: string | null;
}

// 認証フックの戻り値型定義
export interface UseAuth extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  googleLogin: () => Promise<void>;
  logout: () => Promise<void>;
  checkPermission: (action: string) => boolean;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
  resetPassword: (email: string) => Promise<void>;
  registerWithEmailVerification: (userData: RegisterWithEmailVerificationRequest) => Promise<RegisterWithEmailVerificationResponse>;
  verifyEmail: (request: VerifyEmailRequest) => Promise<VerifyEmailResponse>;
  resendVerificationEmail: (userId: string) => Promise<void>;
  // MFA関連メソッド
  enableMFA: (request: MFAEnableRequest) => Promise<MFAEnableResponse>;
  disableMFA: (request: MFADisableRequest) => Promise<MFADisableResponse>;
  verifyMFA: (request: MFAVerifyRequest) => Promise<MFAVerifyResponse>;
  getMFAStatus: (userId: string) => Promise<MFAStatusResponse>;
  generateNewBackupCodes: (request: MFAGenerateBackupCodesRequest) => Promise<MFAGenerateBackupCodesResponse>;
}

// 認証コンテキスト
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<UseAuth | null>(null);

// 認証フック
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): UseAuth => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Token更新用のタイマーRef
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 重複実行防止用のフラグ
  const isCheckingRef = useRef<boolean>(false);

  // 初期化時に認証状態を確認
  useEffect(() => {
    checkAuthState();
    return () => {
      // クリーンアップ: タイマーをクリア
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 初回のみ実行したいため、意図的に依存配列は空

  /**
   * 内部ログアウト処理
   */
  const handleLogoutInternal = useCallback(async () => {
    logger.log('🔍 [DEBUG] 内部ログアウト処理開始');

    setIsAuthenticated(false);
    setUser(null);
    setSessionId(null);
    setError(null);

    // Token更新タイマーをクリア
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Tokenクリア
    AuthService.clearTokens();

    // アカウントデータクリア
    AccountDataService.logout();

    logger.log('✅ [DEBUG] 内部ログアウト処理完了');
  }, []);

  /**
   * 自動Token更新設定
   */
  const setupTokenRefresh = useCallback(() => {
    // 既存のタイマーをクリア
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // 23時間後にToken更新（24時間有効期限の1時間前）
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const refreshResponse = await AuthService.refreshToken();
        AuthService.saveNewAccessToken(refreshResponse.accessToken);

        // 次の更新もスケジュール
        setupTokenRefresh();

        logger.log('✅ Token自動更新完了');
      } catch (error) {
        logger.error('Token自動更新失敗:', error);
        setError('セッションの更新に失敗しました。再度ログインしてください。');
        await handleLogoutInternal();
      }
    }, 23 * 60 * 60 * 1000); // 23時間
  }, [handleLogoutInternal]);

  /**
   * 認証状態確認
   */
  const checkAuthState = useCallback(async () => {
    // 既に実行中の場合はスキップ（React.StrictModeでの重複実行を防ぐ）
    if (isCheckingRef.current) {
      logger.log('🔍 [DEBUG] 認証チェック実行中のためスキップ');
      return;
    }
    isCheckingRef.current = true;

    try {
      setIsInitializing(true);
      setError(null);

      // 🔍 デバッグログ: 初期認証チェック開始
      logger.log('🔍 [DEBUG] 認証状態チェック開始');
      logger.log('🔍 [DEBUG] 環境:', import.meta.env.MODE);
      logger.log('🔍 [DEBUG] URL:', window.location.href);

      // LocalStorage状態チェック（新旧両方のキーをチェック）
      const accessToken = localStorage.getItem('salonhub_access_token') || localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('salonhub_refresh_token') || localStorage.getItem('refreshToken');
      const userData = localStorage.getItem('salonhub_user_data') || localStorage.getItem('userData');
      
      logger.log('🔍 [DEBUG] LocalStorage状態:');
      logger.log('  - accessToken:', accessToken ? 'あり' : 'なし');
      logger.log('  - refreshToken:', refreshToken ? 'あり' : 'なし');
      logger.log('  - userData:', userData ? 'あり' : 'なし');

      // AuthService認証チェック
      const isAuthenticatedByService = AuthService.isAuthenticated();
      logger.log('🔍 [DEBUG] AuthService.isAuthenticated():', isAuthenticatedByService);

      if (isAuthenticatedByService) {
        const userData = await AuthService.getCurrentUser();
        logger.log('🔍 [DEBUG] getCurrentUser結果:', userData ? 'ユーザーデータ取得成功' : 'ユーザーデータなし');

        if (userData) {
          // roleが未設定の場合はデフォルトでownerを設定（開発環境）
          if (!userData.role) {
            userData.role = 'owner';
            logger.log('🔍 [DEBUG] デフォルトロール(owner)を設定');
          }

          // アカウントIDを設定（既にログイン済みの場合）
          AccountDataService.setCurrentAccount(userData.id, userData.email);

          setIsAuthenticated(true);
          setUser(userData);
          setSessionId(generateSecureSessionId());

          // 自動Token更新を設定
          setupTokenRefresh();

          logger.log('✅ [DEBUG] 認証成功:', userData.email, 'role:', userData.role);
        } else {
          // ユーザーデータ取得失敗時はログアウト
          logger.log('❌ [DEBUG] ユーザーデータ取得失敗 - ログアウト処理');
          await handleLogoutInternal();
        }
      } else {
        logger.log('❌ [DEBUG] 未認証状態確定');
        setIsAuthenticated(false);
        setUser(null);
        setSessionId(null);
      }
    } catch (error) {
      logger.error('❌ [DEBUG] 認証状態確認エラー:', error);
      setError('認証状態の確認に失敗しました');
      await handleLogoutInternal();
    } finally {
      logger.log('🔍 [DEBUG] 認証チェック完了 - isInitializing終了');
      setIsInitializing(false);
      isCheckingRef.current = false;
    }
  }, [setupTokenRefresh, handleLogoutInternal]);



  /**
   * ログイン
   */
  const login = useCallback(async (credentials: LoginRequest): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      logger.log('🔍 [DEBUG] ログイン開始:', credentials.email);

      // AuthServiceでログイン
      const response = await AuthService.login(credentials);

      // TokenとユーザーとDataを保存（rememberMeオプション対応）
      AuthService.saveTokens(response, credentials.rememberMe);

      // アカウントIDを設定
      AccountDataService.setCurrentAccount(response.user.id, response.user.email);

      // 初期データセットアップ（アカウント別）
      await InitialDataService.setupInitialData();

      // 状態を更新
      setIsAuthenticated(true);
      setUser(response.user);
      setSessionId(generateSecureSessionId());

      // 自動Token更新を設定
      setupTokenRefresh();

      logger.log('✅ [DEBUG] ログイン成功:', response.user.email);
    } catch (error) {
      logger.error('❌ [DEBUG] ログインエラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = 'ログインに失敗しました。メールアドレスとパスワードを確認してください。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'INVALID_CREDENTIALS');
    } finally {
      setLoading(false);
    }
  }, [setupTokenRefresh]);

  /**
   * Googleログイン
   */
  const googleLogin = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // AuthServiceでGoogleログイン
      const response = await AuthService.googleLogin();

      // Tokenとユーザー情報を保存
      AuthService.saveTokens(response);

      // アカウントIDを設定
      AccountDataService.setCurrentAccount(response.user.id, response.user.email);

      // 初期データセットアップ（アカウント別）
      await InitialDataService.setupInitialData();

      // 状態を更新
      setIsAuthenticated(true);
      setUser(response.user);
      setSessionId(generateSecureSessionId());

      // 自動Token更新を設定
      setupTokenRefresh();

      logger.log('✅ Googleログイン成功:', response.user.email);
    } catch (error) {
      logger.error('Google認証エラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = 'Google認証に失敗しました。もう一度お試しください。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'NETWORK_ERROR');
    } finally {
      setLoading(false);
    }
  }, [setupTokenRefresh]);

  /**
   * ログアウト
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      
      logger.log('🔍 [DEBUG] ログアウト開始');
      
      // AuthServiceでログアウト
      await AuthService.logout();
      
      // 内部ログアウト処理
      await handleLogoutInternal();

      logger.log('✅ [DEBUG] ログアウト完了');

      // ログアウト後のリダイレクトは、認証状態の変更により
      // AppRouterのProtectedRouteが自動的に処理する
    } catch (error) {
      logger.error('ログアウトエラー:', error);

      // エラーが発生してもローカル状態はクリア
      await handleLogoutInternal();
    } finally {
      setLoading(false);
    }
  }, [handleLogoutInternal]);

  /**
   * 権限チェック
   */
  const checkPermission = useCallback((action: string): boolean => {
    if (!user || !isAuthenticated) return false;
    
    // AuthServiceの権限チェックを使用
    return AuthService.hasPermission(action);
  }, [user, isAuthenticated]);

  /**
   * 認証状態更新
   */
  const refreshAuth = useCallback(async (): Promise<void> => {
    await checkAuthState();
  }, [checkAuthState]);

  /**
   * エラークリア
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * パスワードリセット
   */
  const resetPassword = useCallback(async (email: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await AuthService.requestPasswordReset(email);

      logger.log('✅ パスワードリセットメール送信完了');
    } catch (error) {
      logger.error('パスワードリセットエラー:', error);
      
      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }
      
      const errorMessage = 'パスワードリセット要求に失敗しました';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'NETWORK_ERROR');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * メール認証付き新規登録
   */
  const registerWithEmailVerification = useCallback(async (
    userData: RegisterWithEmailVerificationRequest
  ): Promise<RegisterWithEmailVerificationResponse> => {
    try {
      setLoading(true);
      setError(null);

      logger.log('📝 [DEBUG] メール認証付き新規登録開始:', userData.email);

      const response = await AuthService.registerWithEmailVerification(userData);

      logger.log('✅ [DEBUG] メール認証付き新規登録成功:', userData.email);

      return response;
    } catch (error) {
      logger.error('❌ [DEBUG] メール認証付き新規登録エラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = '新規登録に失敗しました。別のメールアドレスをお試しください。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'REGISTRATION_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * メール認証の実行
   */
  const verifyEmail = useCallback(async (
    request: VerifyEmailRequest
  ): Promise<VerifyEmailResponse> => {
    try {
      setLoading(true);
      setError(null);

      logger.log('🔐 [DEBUG] メール認証開始:', request.userId);

      const response = await AuthService.verifyEmail(request);

      if (response.success) {
        // メール認証成功時は認証状態を更新
        await checkAuthState();
        logger.log('✅ [DEBUG] メール認証完了 - 認証状態更新済み');
      }

      return response;
    } catch (error) {
      logger.error('❌ [DEBUG] メール認証エラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = '認証に失敗しました。認証コードを確認してください。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'VERIFICATION_FAILED');
    } finally {
      setLoading(false);
    }
  }, [checkAuthState]);

  /**
   * 認証メール再送信
   */
  const resendVerificationEmail = useCallback(async (userId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      logger.log('📧 [DEBUG] 認証メール再送信開始:', userId);

      await AuthService.resendVerificationEmail({ userId });

      logger.log('✅ [DEBUG] 認証メール再送信完了');
    } catch (error) {
      logger.error('❌ [DEBUG] 認証メール再送信エラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = '認証メールの再送信に失敗しました。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'RESEND_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  // ====================================
  // MFA関連メソッド
  // ====================================

  /**
   * MFA有効化
   */
  const enableMFA = useCallback(async (request: MFAEnableRequest): Promise<MFAEnableResponse> => {
    try {
      setLoading(true);
      setError(null);

      logger.log('🔐 [useAuth] MFA有効化開始:', { userId: request.userId });

      const response = await AuthService.enableMFA(request);

      logger.log('✅ [useAuth] MFA有効化完了');
      return response;
    } catch (error) {
      logger.error('❌ [useAuth] MFA有効化エラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = 'MFAの有効化に失敗しました。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'MFA_ENABLE_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * MFA無効化
   */
  const disableMFA = useCallback(async (request: MFADisableRequest): Promise<MFADisableResponse> => {
    try {
      setLoading(true);
      setError(null);

      logger.log('🔐 [useAuth] MFA無効化開始:', { userId: request.userId });

      const response = await AuthService.disableMFA(request);

      logger.log('✅ [useAuth] MFA無効化完了');
      return response;
    } catch (error) {
      logger.error('❌ [useAuth] MFA無効化エラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = 'MFAの無効化に失敗しました。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'MFA_DISABLE_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * MFA認証
   */
  const verifyMFA = useCallback(async (request: MFAVerifyRequest): Promise<MFAVerifyResponse> => {
    try {
      setLoading(true);
      setError(null);

      logger.log('🔐 [useAuth] MFA認証開始:', { isBackupCode: request.isBackupCode });

      const response = await AuthService.verifyMFA(request);

      if (response.success && response.accessToken && response.user) {
        // アカウントIDを設定
        AccountDataService.setCurrentAccount(response.user.id, response.user.email);

        // 初期データセットアップ（アカウント別）
        await InitialDataService.setupInitialData();

        // MFA認証成功時は認証状態を更新
        setIsAuthenticated(true);
        setUser(response.user);
        logger.log('✅ [useAuth] MFA認証完了 - 認証状態更新済み');
      }

      return response;
    } catch (error) {
      logger.error('❌ [useAuth] MFA認証エラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = 'MFA認証に失敗しました。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'MFA_VERIFICATION_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * MFA状態取得
   */
  const getMFAStatus = useCallback(async (userId: string): Promise<MFAStatusResponse> => {
    try {
      setLoading(true);
      setError(null);

      logger.log('🔐 [useAuth] MFA状態取得開始:', { userId });

      const response = await AuthService.getMFAStatus({ userId });

      logger.log('✅ [useAuth] MFA状態取得完了');
      return response;
    } catch (error) {
      logger.error('❌ [useAuth] MFA状態取得エラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = 'MFA状態の取得に失敗しました。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'MFA_STATUS_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * バックアップコード再生成
   */
  const generateNewBackupCodes = useCallback(async (
    request: MFAGenerateBackupCodesRequest
  ): Promise<MFAGenerateBackupCodesResponse> => {
    try {
      setLoading(true);
      setError(null);

      logger.log('🔐 [useAuth] バックアップコード再生成開始:', { userId: request.userId });

      const response = await AuthService.generateNewBackupCodes(request);

      logger.log('✅ [useAuth] バックアップコード再生成完了');
      return response;
    } catch (error) {
      logger.error('❌ [useAuth] バックアップコード再生成エラー:', error);

      if (error instanceof AuthError) {
        setError(error.message);
        throw error;
      }

      const errorMessage = 'バックアップコードの再生成に失敗しました。';
      setError(errorMessage);
      throw new AuthError(errorMessage, 'BACKUP_CODES_GENERATION_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    isAuthenticated,
    user,
    loading,
    isInitializing,
    error,
    sessionId,
    login,
    googleLogin,
    logout,
    checkPermission,
    refreshAuth,
    clearError,
    resetPassword,
    registerWithEmailVerification,
    verifyEmail,
    resendVerificationEmail,
    // MFA関連メソッド
    enableMFA,
    disableMFA,
    verifyMFA,
    getMFAStatus,
    generateNewBackupCodes,
  };
};

/**
 * 認証フックを使用するためのコンテキストフック
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuthContext = (): UseAuth => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext は AuthProvider 内で使用してください');
  }
  return context;
};

/**
 * AuthProviderコンポーネント
 */
export interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();
  
  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
};